// The Wizard / Installer permissions: one per operation per wizard page.
//
// LiftCloud declares them in permissions/catalogue_wizard.go as
// `wizard-<page>-<op>`, where <page> is the step key kebab-cased. Each one
// also opens exactly the API calls its page makes, so a role granted
// `wizard-banks-create` can finish the Banks page's create flow without the
// Edge Management bank permissions. Roles saved before these keys existed hold
// an operation when they already hold every call it makes, so the wizard
// keeps working for them unchanged.
//
// Keyed by StepKey, so a step added to WIZARD_STEPS is a compile error here
// until someone decides its permissions (and adds them to the LiftCloud
// catalogue, whose TestWizardStepsMatchTheFrontend pins the same list).
import { hasPermission } from "../../types/permissions";
import type { StepKey } from "./wizardSteps";

export type WizardOp = "view" | "create" | "update" | "delete";

const CRUD: readonly WizardOp[] = ["view", "create", "update", "delete"];

/** Each step's page key in the catalogue, and the operations it has. */
export const WIZARD_STEP_PERMISSIONS: Record<StepKey, { page: string; ops: readonly WizardOp[] }> =
  {
    Organization: { page: "organization", ops: CRUD },
    Server: { page: "server", ops: CRUD },
    Adapters: { page: "adapters", ops: CRUD },
    Channels: { page: "channels", ops: CRUD },
    Banks: { page: "banks", ops: CRUD },
    ChannelModifiers: { page: "channel-modifiers", ops: CRUD },
    Lifts: { page: "lifts", ops: CRUD },
    Debounce: { page: "debounce", ops: CRUD },
    // BACnet mappings live on the adapter: there is nothing to add or delete.
    Bacnet: { page: "bacnet", ops: ["view", "update"] },
    Users: { page: "users-roles", ops: CRUD },
    Notifications: { page: "notifications", ops: CRUD },
    Actions: { page: "actions", ops: CRUD },
    MonitorGrid: { page: "monitor-grid", ops: CRUD },
    // Review only reads; its one write is finishing the setup.
    Review: { page: "review", ops: ["view", "update"] },
  };

/** The permission gating `op` on `step`, or null when the step has no such operation. */
export function wizardPermissionKey(step: StepKey, op: WizardOp): string | null {
  const { page, ops } = WIZARD_STEP_PERMISSIONS[step];
  return ops.includes(op) ? `wizard-${page}-${op}` : null;
}

export type WizardStepAccess = Record<WizardOp, boolean>;

/** What the user may do on `step`. MASTER ADMIN may do everything. */
export function wizardStepAccess(
  step: StepKey,
  permissionSet: string[],
  masterAdmin: boolean,
): WizardStepAccess {
  const can = (op: WizardOp): boolean => {
    const key = wizardPermissionKey(step, op);
    return key !== null && (masterAdmin || hasPermission(key, permissionSet));
  };

  return { view: can("view"), create: can("create"), update: can("update"), delete: can("delete") };
}
